import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  ArrowLeft, Check, Palette, Layout, Settings2, Eye
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const TEMPLATES = [
  { id: "modern",  label: "Modern",  desc: "Clean with accent bar" },
  { id: "classic", label: "Classic", desc: "Traditional & formal" },
  { id: "minimal", label: "Minimal", desc: "Ultra-clean, subtle" },
  { id: "bold",    label: "Bold",    desc: "Strong color headers" },
];

const COLOR_PRESETS = [
  { primary: "#0046BE", accent: "#FFCC00", label: "Blue & Gold" },
  { primary: "#059669", accent: "#34d399", label: "Emerald" },
  { primary: "#7c3aed", accent: "#c4b5fd", label: "Purple" },
  { primary: "#dc2626", accent: "#fca5a5", label: "Red" },
  { primary: "#0891b2", accent: "#67e8f9", label: "Cyan" },
  { primary: "#1a1a2e", accent: "#e94560", label: "Dark" },
  { primary: "#f97316", accent: "#fdba74", label: "Orange" },
  { primary: "#0f172a", accent: "#38bdf8", label: "Navy" },
];

const InvoicePreviewPanel = ({ open, onClose, isDark, headers, invoice = null }) => {
  const [settings, setSettings] = useState({
    template: "modern", primary_color: "#0046BE", accent_color: "#FFCC00", show_logo: true,
  });
  const [activeTab, setActiveTab] = useState("template");
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState({});
  const [locations, setLocations] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState(null);

  // Canonical payment history — the SAME endpoint data the PDFs render
  useEffect(() => {
    if (!open) return;
    setPaymentHistory(null);
    if (invoice?.invoice_id && (invoice.document_type || "invoice") !== "estimate") {
      axios.get(`${API}/invoices/${invoice.invoice_id}/payment-history`, { headers })
        .then((r) => setPaymentHistory(r.data))
        .catch(() => setPaymentHistory(null));
    }
  }, [open, invoice]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      axios.get(`${API}/invoices/template/settings`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/settings/business`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/store/locations`, { headers }).catch(() => ({ data: {} })),
    ]).then(([tRes, bRes, lRes]) => {
      const t = tRes.data || {};
      setSettings({
        template: t.template || "modern",
        primary_color: t.primary_color || "#0046BE",
        accent_color: t.accent_color || "#FFCC00",
        show_logo: t.show_logo !== false,
      });
      setBusiness(bRes.data || {});
      const list = Array.isArray(lRes.data) ? lRes.data : (lRes.data?.locations || []);
      setLocations(list);
    });
  }, [open]);

  // Mirror the backend's apply_location_branding: when the doc belongs to a
  // business/brand (location_id), the brand's identity + logo + colors
  // override the HQ defaults so the preview matches the real PDF/email.
  const brandLoc = useMemo(() => {
    if (!invoice?.location_id) return null;
    return locations.find((l) => l.location_id === invoice.location_id) || null;
  }, [invoice, locations]);

  const effBusiness = useMemo(() => {
    // Per-document custom name (privacy mode): only the custom name shows —
    // logo, address, phone and email are hidden, mirroring the backend.
    const customName = invoice?.company_name_override ? (invoice.company_name || "").trim() : "";
    if (customName) return { business_name: customName, company_name: customName };
    if (!brandLoc) return business;
    const out = { ...business };
    if (brandLoc.business_name) {
      out.business_name = brandLoc.business_name;
      out.company_name = brandLoc.business_name;
    }
    if (brandLoc.business_email) out.business_email = brandLoc.business_email;
    const addrParts = [
      brandLoc.address,
      [brandLoc.city, brandLoc.state].filter(Boolean).join(", "),
      brandLoc.zip_code,
    ].filter(Boolean);
    if (addrParts.length) out.business_address = addrParts.join(", ");
    if (brandLoc.phone) out.business_phone = brandLoc.phone;
    if (brandLoc.logo_file || brandLoc.logo_data) out.location_logo_id = brandLoc.location_id;
    return out;
  }, [business, brandLoc]);

  const displaySettings = useMemo(() => {
    if (!brandLoc) return settings;
    return {
      ...settings,
      primary_color: brandLoc.primary_color || settings.primary_color,
      accent_color: brandLoc.accent_color || settings.accent_color,
    };
  }, [settings, brandLoc]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/invoices/template/settings`, settings, { headers });
      toast.success("Template saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: "template", icon: Layout, label: "Template" },
    { id: "color",    icon: Palette, label: "Color" },
    { id: "options",  icon: Settings2, label: "Options" },
  ];

  // Sample data for preview
  const sampleInvoice = invoice || {
    invoice_number: "INV-00018",
    document_type: "invoice",
    customer_name: "John Smith",
    customer_address: "123 Main St, Suite 4",
    customer_phone: "(555) 123-4567",
    customer_email: "john@example.com",
    created_at: new Date().toISOString(),
    due_date: new Date(Date.now() + 30 * 86400000).toISOString(),
    status: "sent",
    items: [
      { description: "Service Call", quantity: 1, unit_price: 150, discount: 0 },
      { description: "Parts - Lock Assembly", quantity: 2, unit_price: 45, discount: 10 },
    ],
    subtotal: 240,
    discount_total: 9,
    tax_rate: 8.5,
    tax_amount: 19.64,
    total: 250.64,
    payment_terms: "Due on receipt",
    notes: "Thank you for your business!",
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl p-0 gap-0 max-h-[95vh] sm:max-h-[95vh] h-[100dvh] sm:h-auto overflow-hidden [&>button.absolute]:hidden" data-testid="preview-panel">
        <VisuallyHidden.Root><h2>Preview & Customize</h2></VisuallyHidden.Root>
        {/* Top bar */}
        <div className={`flex items-center justify-between px-4 sm:px-5 py-3 border-b shrink-0 ${isDark ? "border-zinc-800" : "border-gray-100"}`}>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 -ml-1" data-testid="preview-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className={`text-sm sm:text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>Preview & Customize</h2>
          </div>
          <Button size="sm" onClick={saveSettings} disabled={saving} className="bg-blue-500 text-white hover:bg-blue-600 gap-1.5 text-xs sm:text-sm h-8 sm:h-9 px-3" data-testid="save-template-btn">
            <Check className="w-3.5 h-3.5" /> {saving ? "..." : "Save"}
          </Button>
        </div>

        {/* Mobile: stacked layout. Desktop: side-by-side */}
        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden" style={{ maxHeight: "calc(95vh - 52px)" }}>
          {/* Preview Pane */}
          <div className={`flex-1 overflow-y-auto p-4 sm:p-6 ${isDark ? "bg-zinc-950" : "bg-gray-100"}`}>
            <div className="max-w-[520px] mx-auto">
              <InvoiceHTMLPreview
                invoice={sampleInvoice}
                settings={displaySettings}
                business={effBusiness}
                paymentHistory={paymentHistory}
              />
            </div>
          </div>

          {/* Customize Panel — bottom sheet on mobile, sidebar on desktop */}
          <div className={`w-full sm:w-[320px] flex flex-col border-t sm:border-t-0 sm:border-l shrink-0 max-h-[45vh] sm:max-h-none ${isDark ? "border-zinc-800 bg-zinc-900" : "border-gray-200 bg-white"}`}>
            {/* Tabs */}
            <div className={`flex border-b shrink-0 ${isDark ? "border-zinc-800" : "border-gray-100"}`}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 sm:py-3 sm:flex-col sm:gap-1 text-xs sm:text-[10px] font-medium uppercase tracking-wider transition-colors ${
                    activeTab === tab.id
                      ? "text-blue-500 border-b-2 border-blue-500"
                      : isDark ? "text-zinc-500 hover:text-zinc-300" : "text-gray-400 hover:text-gray-600"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <ScrollArea className="flex-1 p-4">
              {activeTab === "template" && (
                <div className="space-y-2.5" data-testid="template-list">
                  {TEMPLATES.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSettings(s => ({ ...s, template: t.id }))}
                      className={`w-full text-left rounded-xl p-3.5 transition-all ${
                        settings.template === t.id
                          ? isDark
                            ? "bg-blue-500/10 ring-2 ring-blue-500/50 border border-blue-500/30"
                            : "bg-blue-50 ring-2 ring-blue-500/30 border border-blue-200"
                          : isDark
                            ? "bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50"
                            : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      {/* Mini preview bar */}
                      <div className="flex items-center gap-3 mb-2">
                        <TemplatePreviewThumb template={t.id} primary={settings.primary_color} accent={settings.accent_color} />
                        <div>
                          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{t.label}</p>
                          <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>{t.desc}</p>
                        </div>
                      </div>
                      {settings.template === t.id && (
                        <div className="flex items-center gap-1 text-blue-500 text-[10px] font-medium mt-1">
                          <Check className="w-3 h-3" /> Active
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {activeTab === "color" && (
                <div className="space-y-5" data-testid="color-panel">
                  <div>
                    <Label className={`text-xs uppercase tracking-wide font-medium mb-3 block ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                      Color Presets
                    </Label>
                    <div className="grid grid-cols-4 gap-2">
                      {COLOR_PRESETS.map((preset, i) => {
                        const isActive = settings.primary_color === preset.primary && settings.accent_color === preset.accent;
                        return (
                          <button
                            key={i}
                            onClick={() => setSettings(s => ({ ...s, primary_color: preset.primary, accent_color: preset.accent }))}
                            className={`group relative rounded-lg p-1.5 transition-all ${
                              isActive
                                ? "ring-2 ring-blue-500 ring-offset-2 " + (isDark ? "ring-offset-zinc-900" : "ring-offset-white")
                                : ""
                            }`}
                            title={preset.label}
                            data-testid={`color-preset-${i}`}
                          >
                            <div className="flex rounded-md overflow-hidden h-8">
                              <div className="flex-1" style={{ backgroundColor: preset.primary }} />
                              <div className="w-3" style={{ backgroundColor: preset.accent }} />
                            </div>
                            <p className={`text-[9px] mt-1 text-center truncate ${isDark ? "text-zinc-500" : "text-gray-400"}`}>{preset.label}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label className={`text-xs ${isDark ? "text-zinc-400" : "text-gray-500"}`}>Primary Color</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <input
                          type="color"
                          value={settings.primary_color}
                          onChange={(e) => setSettings(s => ({ ...s, primary_color: e.target.value }))}
                          className="w-10 h-8 rounded border-0 cursor-pointer p-0"
                          data-testid="color-primary"
                        />
                        <span className={`text-xs font-mono ${isDark ? "text-zinc-400" : "text-gray-500"}`}>{settings.primary_color}</span>
                      </div>
                    </div>
                    <div>
                      <Label className={`text-xs ${isDark ? "text-zinc-400" : "text-gray-500"}`}>Accent Color</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <input
                          type="color"
                          value={settings.accent_color}
                          onChange={(e) => setSettings(s => ({ ...s, accent_color: e.target.value }))}
                          className="w-10 h-8 rounded border-0 cursor-pointer p-0"
                          data-testid="color-accent"
                        />
                        <span className={`text-xs font-mono ${isDark ? "text-zinc-400" : "text-gray-500"}`}>{settings.accent_color}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "options" && (
                <div className="space-y-5" data-testid="options-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>Show Logo</p>
                      <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Display company logo on invoices</p>
                    </div>
                    <Switch
                      checked={settings.show_logo}
                      onCheckedChange={(v) => setSettings(s => ({ ...s, show_logo: v }))}
                      data-testid="toggle-logo"
                    />
                  </div>
                  <div className={`rounded-lg p-3 text-xs ${isDark ? "bg-zinc-800/50 text-zinc-400" : "bg-blue-50 text-blue-700"}`}>
                    To update your company logo, go to Settings &rarr; Business Settings
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Template thumbnail previews */
const TemplatePreviewThumb = ({ template, primary, accent }) => {
  const w = 40; const h = 52;
  if (template === "modern") {
    return (
      <svg width={w} height={h} viewBox="0 0 40 52" fill="none" className="rounded flex-shrink-0">
        <rect width="40" height="52" rx="3" fill="#f8f9fc" />
        <rect y="0" width="40" height="3" rx="1" fill={accent} />
        <rect x="4" y="6" width="14" height="3" rx="1" fill="#1a1a2e" />
        <rect x="22" y="6" width="14" height="5" rx="1" fill={primary} />
        <rect x="4" y="14" width="32" height="3" rx="1" fill="#e5e7eb" />
        <rect x="4" y="20" width="32" height="2" rx="1" fill={primary} opacity="0.2" />
        <rect x="4" y="24" width="32" height="2" rx="1" fill="#f0f0f0" />
        <rect x="4" y="28" width="32" height="2" rx="1" fill="#f0f0f0" />
        <rect x="22" y="36" width="14" height="4" rx="1" fill={primary} />
        <rect x="4" y="44" width="32" height="2" rx="1" fill="#e5e7eb" />
      </svg>
    );
  }
  if (template === "classic") {
    return (
      <svg width={w} height={h} viewBox="0 0 40 52" fill="none" className="rounded flex-shrink-0">
        <rect width="40" height="52" rx="3" fill="#faf9f7" />
        <rect x="3" y="3" width="34" height="46" rx="2" stroke="#d4d0ca" strokeWidth="0.5" fill="none" />
        <rect x="6" y="6" width="12" height="3" rx="1" fill="#1a1a2e" />
        <rect x="24" y="6" width="10" height="3" rx="1" fill={primary} opacity="0.3" />
        <line x1="6" y1="14" x2="34" y2="14" stroke="#d4d0ca" strokeWidth="0.5" />
        <rect x="6" y="18" width="28" height="2" rx="1" fill={primary} />
        <rect x="6" y="22" width="28" height="2" rx="1" fill="#f0ede8" />
        <rect x="6" y="26" width="28" height="2" rx="1" fill="#f0ede8" />
        <line x1="6" y1="32" x2="34" y2="32" stroke="#d4d0ca" strokeWidth="0.5" />
        <rect x="22" y="35" width="12" height="4" rx="1" fill={primary} />
      </svg>
    );
  }
  if (template === "minimal") {
    return (
      <svg width={w} height={h} viewBox="0 0 40 52" fill="none" className="rounded flex-shrink-0">
        <rect width="40" height="52" rx="3" fill="white" />
        <rect x="4" y="6" width="10" height="2" rx="1" fill="#374151" />
        <rect x="28" y="6" width="8" height="2" rx="1" fill="#9ca3af" />
        <rect x="4" y="14" width="32" height="1" fill="#f3f4f6" />
        <rect x="4" y="18" width="32" height="1.5" rx="0.5" fill="#e5e7eb" />
        <rect x="4" y="22" width="32" height="1.5" rx="0.5" fill="#f9fafb" />
        <rect x="4" y="26" width="32" height="1.5" rx="0.5" fill="#e5e7eb" />
        <rect x="4" y="34" width="32" height="1" fill="#e5e7eb" />
        <rect x="24" y="38" width="12" height="3" rx="1" fill={primary} />
      </svg>
    );
  }
  // bold
  return (
    <svg width={w} height={h} viewBox="0 0 40 52" fill="none" className="rounded flex-shrink-0">
      <rect width="40" height="52" rx="3" fill="white" />
      <rect width="40" height="14" rx="3" fill={primary} />
      <rect x="4" y="4" width="14" height="2" rx="1" fill="white" opacity="0.9" />
      <rect x="4" y="8" width="20" height="3" rx="1" fill="white" opacity="0.7" />
      <rect x="4" y="18" width="32" height="2" rx="1" fill={accent} />
      <rect x="4" y="22" width="32" height="1.5" rx="0.5" fill="#f0f0f0" />
      <rect x="4" y="26" width="32" height="1.5" rx="0.5" fill="#f0f0f0" />
      <rect x="4" y="30" width="32" height="1.5" rx="0.5" fill="#f0f0f0" />
      <rect width="40" height="10" y="38" rx="3" fill={primary} opacity="0.1" />
      <rect x="22" y="41" width="14" height="4" rx="1" fill={primary} />
    </svg>
  );
};

/* Live HTML Invoice Preview */
const PREVIEW_METHOD_LABELS = {
  "stripe": "Card (online)", "card (stripe)": "Credit Card", "card": "Credit Card",
  "credit card": "Credit Card", "debit card": "Debit Card", "cash": "Cash",
  "check": "Check", "cheque": "Check", "ach": "ACH", "zelle": "Zelle",
  "venmo": "Venmo", "bank transfer": "Bank Transfer", "bank_transfer": "Bank Transfer",
  "financing": "Financing", "other": "Other",
};

const previewMethod = (p) => {
  if (p.card_last4) {
    const brand = p.card_brand ? p.card_brand[0].toUpperCase() + p.card_brand.slice(1) : "Card";
    return `${brand} \u2022\u2022\u2022\u2022 ${p.card_last4}`;
  }
  const raw = (p.method || "").trim();
  if (!raw) return "\u2014";
  return PREVIEW_METHOD_LABELS[raw.toLowerCase()] || raw;
};

// Client-side fallback mirroring the backend builder (used for unsaved docs)
const buildClientHistory = (inv) => {
  const total = inv.total || 0;
  const pays = (inv.payments || [])
    .filter((p) => p && (p.amount || 0) > 0)
    .sort((a, b) => String(a.payment_date || "").localeCompare(String(b.payment_date || "")));
  let running = 0;
  const rows = pays.map((p, i) => {
    running += p.amount || 0;
    const note = (p.note || "").toLowerCase();
    const type = note.includes("deposit")
      ? "Deposit"
      : running >= total - 0.009 ? (i === 0 ? "Payment" : "Final Payment") : "Partial Payment";
    return {
      date: (p.payment_date || p.created_at || "").slice(0, 10),
      type, method: previewMethod(p), amount: p.amount, is_refund: false, status: "Paid",
    };
  });
  let gross = Math.max(running, inv.amount_paid || 0);
  if (inv.status === "paid") gross = Math.max(gross, total);
  const balance = Math.max(total - gross, 0);
  const payment_status =
    balance <= 0.009 && (gross > 0 || inv.status === "paid") && total > 0
      ? "Paid" : gross > 0 ? "Partially Paid" : "Unpaid";
  return { rows, gross_paid: gross, total_refunded: 0, net_paid: gross, balance_due: balance, payment_status };
};

const InvoiceHTMLPreview = ({ invoice, settings, business, paymentHistory = null }) => {
  const { template, primary_color, accent_color, show_logo } = settings;
  const isEst = (invoice.document_type || "invoice") === "estimate";
  const hist = (!isEst && paymentHistory) || buildClientHistory(invoice);
  const headerColor = isEst ? "#f97316" : primary_color;
  const items = invoice.items || [];
  const hasDiscount = items.some(i => (i.discount || 0) > 0);
  const companyName = business.business_name || business.company_name || "Your Company";

  // Resolve effective logo. A brand/location logo takes precedence, then the
  // uploaded HQ file (served via /api/invoices/business-logo), then any legacy
  // text URL — same precedence as the PDF generator and the public endpoints,
  // so the preview is an honest representation of what the customer sees.
  const logoSrc = settings.show_logo
    ? (business.location_logo_id
        ? `${API}/invoices/template/location-logo/${business.location_logo_id}`
        : business.business_logo_file && business.user_id
          ? `${API}/invoices/template/business-logo/${business.user_id}?v=${business.business_logo_updated || ""}`
          : (business.logo_url || ""))
    : "";
  const businessLineParts = [business.business_phone, business.business_email].filter(Boolean);

  const createdDate = invoice.created_at
    ? format(new Date(invoice.created_at), "MMMM d, yyyy") : "N/A";
  const dueDate = invoice.due_date
    ? format(new Date(invoice.due_date), "MMMM d, yyyy") : "Upon Receipt";

  const sharedStyles = {
    fontFamily: template === "classic" ? "'Georgia', serif" : "'Helvetica Neue', Arial, sans-serif",
    fontSize: "11px",
    color: "#0a0a1a",
  };

  return (
    <div
      style={{
        ...sharedStyles,
        background: template === "classic" ? "#faf9f7" : "white",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        border: template === "classic" ? "1px solid #d4d0ca" : "none",
      }}
      data-testid="invoice-preview"
    >
      {/* Top accent */}
      {template === "modern" && (
        <div style={{ height: 4, background: accent_color }} />
      )}
      {template === "bold" && (
        <div style={{ background: headerColor, padding: "24px 28px", color: "white" }}>
          {logoSrc && (
            <img src={logoSrc} alt="Logo"
              style={{ maxHeight: 38, maxWidth: 160, marginBottom: 10, objectFit: "contain", background: "rgba(255,255,255,0.92)", borderRadius: 4, padding: "2px 6px" }}
            />
          )}
          <div style={{ fontSize: "20px", fontWeight: 700, marginBottom: 4 }}>{companyName}</div>
          {business.business_address && (
            <div style={{ fontSize: "10px", opacity: 0.85, whiteSpace: "pre-line", lineHeight: 1.35, marginBottom: 4 }}>
              {business.business_address}
            </div>
          )}
          {businessLineParts.length > 0 && (
            <div style={{ fontSize: "10px", opacity: 0.8 }}>
              {businessLineParts.join(" | ")}
            </div>
          )}
          <div style={{ fontSize: "28px", fontWeight: 700, marginTop: 12, opacity: 0.9 }}>
            {isEst ? "ESTIMATE" : "INVOICE"}
          </div>
          <div style={{ fontSize: "11px", opacity: 0.7 }}>{invoice.invoice_number}</div>
        </div>
      )}

      <div style={{ padding: template === "bold" ? "20px 28px 28px" : "28px" }}>
        {/* Header - not for bold (already rendered) */}
        {template !== "bold" && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {logoSrc && (
                <img src={logoSrc} alt="Logo"
                  style={{ maxHeight: 44, maxWidth: 170, objectFit: "contain", marginBottom: 8, display: "block" }}
                />
              )}
              <div style={{ fontSize: template === "classic" ? "16px" : "18px", fontWeight: 700, color: "#0a0a1a" }}>{companyName}</div>
              {business.business_address && (
                <div style={{ fontSize: "9px", color: "#6b7280", marginTop: 2, whiteSpace: "pre-line", lineHeight: 1.4 }}>
                  {business.business_address}
                </div>
              )}
              {businessLineParts.length > 0 && (
                <div style={{ fontSize: "9px", color: "#6b7280", marginTop: 1 }}>
                  {businessLineParts.join(" | ")}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: template === "minimal" ? "18px" : "26px", fontWeight: 700, color: headerColor }}>
                {isEst ? "ESTIMATE" : "INVOICE"}
              </div>
              <div style={{ fontSize: "10px", color: "#6b7280" }}>{invoice.invoice_number}</div>
            </div>
          </div>
        )}

        {/* Separator */}
        {template === "classic" && <hr style={{ border: "none", borderTop: "1px solid #d4d0ca", margin: "0 0 20px" }} />}

        {/* Bill To + Details */}
        <div style={{
          display: "flex", justifyContent: "space-between", gap: 20,
          background: template === "minimal" ? "transparent" : "#f8f9fc",
          borderRadius: 8, padding: template === "minimal" ? "0 0 16px" : 16,
          marginBottom: 20,
          border: template === "minimal" ? "none" : "none",
          borderBottom: template === "minimal" ? "1px solid #f3f4f6" : "none",
        }}>
          <div>
            <div style={{ fontSize: "8px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em", marginBottom: 4 }}>BILL TO</div>
            <div style={{ fontSize: "11px", fontWeight: 700 }}>{invoice.customer_name}</div>
            {invoice.customer_address && <div style={{ fontSize: "9px", color: "#6b7280" }}>{invoice.customer_address}</div>}
            {(() => {
              // Compose "City, ST Zip" — show whatever fields are populated;
              // skip silently if none are. Supports both the form-preview
              // shape (`customer_city/customer_state/customer_zip`) and
              // the backend-returned shape (`address_city/address_state/zip_code`).
              const city = invoice.customer_city || invoice.address_city || "";
              const stateAbbr = invoice.customer_state || invoice.address_state || "";
              const zip = invoice.customer_zip || invoice.zip_code || "";
              const region = [stateAbbr, zip].filter(Boolean).join(" ");
              const cityStateZip = [city, region].filter(Boolean).join(", ");
              return cityStateZip
                ? <div style={{ fontSize: "9px", color: "#6b7280" }}>{cityStateZip}</div>
                : null;
            })()}
            <div style={{ fontSize: "9px", color: "#6b7280" }}>
              {[invoice.customer_phone, invoice.customer_email].filter(Boolean).join(" | ")}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "8px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em", marginBottom: 4 }}>DETAILS</div>
            <div style={{ fontSize: "10px" }}>Date: {createdDate}</div>
            <div style={{ fontSize: "10px" }}>{isEst ? "Valid Until" : "Due"}: {dueDate}</div>
            <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase" }}>Status: {invoice.status}</div>
          </div>
        </div>

        {/* Items table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
          <thead>
            <tr style={{
              background: template === "minimal" ? "transparent" : headerColor,
              color: template === "minimal" ? "#374151" : "white",
            }}>
              <th style={{ padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>#</th>
              <th style={{ padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>Description</th>
              <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>Qty</th>
              <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>Price</th>
              {hasDiscount && <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>Disc</th>}
              <th style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, fontSize: "9px",
                borderBottom: template === "minimal" ? "2px solid #e5e7eb" : "none" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const disc = item.discount || 0;
              const lineTotal = item.quantity * item.unit_price * (1 - disc / 100);
              return (
                <tr key={i} style={{
                  background: i % 2 === 1 ? (template === "minimal" ? "#fafafa" : "#f8f9fc") : "transparent",
                  borderBottom: "0.5px solid #e5e7eb",
                }}>
                  <td style={{ padding: "8px", textAlign: "center", color: "#6b7280" }}>{i + 1}</td>
                  <td style={{ padding: "8px" }}>
                    <div>{item.description}</div>
                    {item.long_description && (
                      <div style={{ marginTop: "4px", fontSize: "11px", color: "#6b7280", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                        {item.long_description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px", textAlign: "right" }}>{item.quantity}</td>
                  <td style={{ padding: "8px", textAlign: "right" }}>${item.unit_price.toFixed(2)}</td>
                  {hasDiscount && <td style={{ padding: "8px", textAlign: "right", color: disc > 0 ? "#f97316" : "#9ca3af" }}>
                    {disc > 0 ? `${disc}%` : "-"}
                  </td>}
                  <td style={{ padding: "8px", textAlign: "right", fontWeight: 600 }}>${lineTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <div style={{ width: "220px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#6b7280" }}>
              <span>Subtotal</span><span>${(invoice.subtotal || 0).toFixed(2)}</span>
            </div>
            {(invoice.discount_total || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#f97316" }}>
                <span>{invoice.discount_type === "flat" ? "Discount (flat)" : (invoice.discount_value || 0) > 0 ? `Discount (${invoice.discount_value}%)` : "Discount"}</span><span>-${invoice.discount_total.toFixed(2)}</span>
              </div>
            )}
            {(invoice.tax_rate || 0) > 0 && (invoice.non_taxable_subtotal || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#6b7280" }}>
                <span>Taxable Subtotal</span><span>${(invoice.taxable_subtotal || 0).toFixed(2)}</span>
              </div>
            )}
            {(invoice.tax_rate || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#6b7280" }}>
                <span>Tax ({invoice.tax_rate}%)</span><span>${(invoice.tax_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoice.credit_fee_enabled && (invoice.credit_fee_amount || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#6b7280" }}>
                <span>{invoice.credit_fee_type === "percentage" ? `Credit Fee (${invoice.credit_fee_value}%)` : "Credit Fee"}</span>
                <span>${(invoice.credit_fee_amount || 0).toFixed(2)}</span>
              </div>
            )}
            <div style={{
              display: "flex", justifyContent: "space-between", padding: "10px 0 0",
              marginTop: 6, borderTop: `2px solid ${headerColor}`,
              fontWeight: 700,
            }}>
              <span style={{ fontSize: "12px" }}>{isEst ? "ESTIMATE TOTAL" : "TOTAL"}</span>
              <span style={{ fontSize: "15px", color: headerColor }}>${(invoice.total || 0).toFixed(2)}</span>
            </div>
            {/* Deposit / payments / balance — shown on fully-paid invoices too */}
            {!isEst && ((hist.gross_paid || 0) > 0 || (invoice.deposit_required || 0) > 0) && (
              <div data-testid="preview-payment-rows">
                {(invoice.deposit_required || 0) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#6b7280" }}>
                    <span>Deposit Required</span><span>${(invoice.deposit_required || 0).toFixed(2)}</span>
                  </div>
                )}
                {(hist.gross_paid || 0) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "10px", color: "#059669" }}>
                    <span>Amount Paid</span><span>-${(hist.gross_paid || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0 0", fontWeight: 700, color: hist.balance_due <= 0.009 ? "#059669" : "#b45309" }}>
                  <span style={{ fontSize: "11px" }}>BALANCE DUE</span>
                  <span style={{ fontSize: "13px" }}>${(hist.balance_due || 0).toFixed(2)}</span>
                </div>
                {hist.payment_status === "Partially Paid" && (
                  <div style={{ textAlign: "right", fontSize: "8px", color: "#b45309", marginTop: 2, fontWeight: 600, letterSpacing: "0.04em" }}>
                    PARTIALLY PAID
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Payment History — identical to the customer PDF section */}
        {!isEst && (
          <div style={{ marginTop: 24 }} data-testid="preview-payment-history">
            <div style={{ fontSize: "8px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em", marginBottom: 6 }}>PAYMENT HISTORY</div>
            {hist.rows.length === 0 ? (
              <div style={{ fontSize: "9px", color: "#9ca3af" }}>No payments received yet.</div>
            ) : (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      {["Date", "Type", "Method", "Status"].map((h) => (
                        <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, borderBottom: "1px solid #d1d5db" }}>{h}</th>
                      ))}
                      <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, borderBottom: "1px solid #d1d5db" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.rows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "0.5px solid #eee" }}>
                        <td style={{ padding: "6px 8px" }}>{r.date ? format(new Date(`${r.date}T00:00:00`), "MMM d, yyyy") : ""}</td>
                        <td style={{ padding: "6px 8px" }}>{r.type}</td>
                        <td style={{ padding: "6px 8px" }}>{r.method}</td>
                        <td style={{ padding: "6px 8px" }}>{r.status}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: r.is_refund ? "#dc2626" : "#111827" }}>
                          {r.amount < 0 ? "-" : ""}${Math.abs(r.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, marginTop: 8, fontSize: "9px", color: "#374151" }}>
                  <span>Total Paid: <b>${(hist.gross_paid || 0).toFixed(2)}</b></span>
                  {(hist.total_refunded || 0) > 0 && (
                    <>
                      <span>Total Refunded: <b style={{ color: "#dc2626" }}>-${hist.total_refunded.toFixed(2)}</b></span>
                      <span>Net Paid: <b>${(hist.net_paid || 0).toFixed(2)}</b></span>
                    </>
                  )}
                  <span>Balance Due: <b style={{ color: hist.balance_due <= 0.009 ? "#059669" : "#d97706" }}>${(hist.balance_due || 0).toFixed(2)}</b></span>
                  <span>Payment Status: <b>{hist.payment_status}</b></span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Notes / Terms */}
        {(invoice.payment_terms || invoice.notes) && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
            {invoice.payment_terms && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: "8px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em", marginBottom: 2 }}>PAYMENT TERMS</div>
                <div style={{ fontSize: "9px", color: "#6b7280" }}>{invoice.payment_terms}</div>
              </div>
            )}
            {invoice.notes && (
              <div>
                <div style={{ fontSize: "8px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em", marginBottom: 2 }}>NOTES</div>
                <div style={{ fontSize: "9px", color: "#6b7280" }}>{invoice.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 28, paddingTop: 12, borderTop: "1px solid #e5e7eb", textAlign: "center" }}>
          <div style={{ fontSize: "8px", color: "#9ca3af" }}>Thank you for your business! | Powered by Fieldz AI</div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewPanel;
